/* eslint-disable react/prop-types */
import React, { useCallback, useMemo } from 'react';
import { Box, Textarea } from '@chakra-ui/react';
import { Handle, Position } from 'reactflow';
import { shallow } from 'zustand/shallow';
import useFlowchartStore from '../../Hooks/Zustand/flowchartStore';

// ==================== HELPER FUNCTIONS ====================

/**
 * Calculate luminance to determine if color is light or dark
 */
const getLuminance = (hex) => {
  const rgb = parseInt(hex.slice(1), 16);
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = (rgb >> 0) & 0xff;
  
  return 0.299 * r + 0.587 * g + 0.114 * b;
};

/**
 * Get optimal text color based on background
 */
const getTextColor = (bgColor) => {
  if (!bgColor || bgColor === 'transparent') return '#ffffff';
  const luminance = getLuminance(bgColor);
  return luminance > 128 ? '#000000' : '#ffffff';
};

/**
 * Create gradient effect
 */
const createGradient = (color) => {
  if (!color) return null;
  
  // Lighter and darker shades
  const lighterColor = `${color}dd`;
  const darkerColor = `${color}bb`;
  
  return `linear-gradient(135deg, ${lighterColor} 0%, ${color} 50%, ${darkerColor} 100%)`;
};

// ==================== SHAPE RENDERER ====================
function useShape({ type, width, height, color = '#9ca8b3', selected }) {
  const gradient = useMemo(() => createGradient(color), [color]);
  
  const styles = {
    fill: gradient ? `url(#gradient-${type})` : color,
    stroke: selected ? '#4299e1' : 'rgba(0,0,0,0.2)',
    strokeWidth: selected ? 3 : 1,
    filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.2))',
  };

  const GradientDef = () => (
    <defs>
      <linearGradient id={`gradient-${type}`} x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style={{ stopColor: `${color}dd`, stopOpacity: 1 }} />
        <stop offset="50%" style={{ stopColor: color, stopOpacity: 1 }} />
        <stop offset="100%" style={{ stopColor: `${color}bb`, stopOpacity: 1 }} />
      </linearGradient>
    </defs>
  );

  const shapes = {
    circle: (
      <>
        <GradientDef />
        <ellipse
          cx={width / 2}
          cy={height / 2}
          rx={width / 2 - 2}
          ry={height / 2 - 2}
          {...styles}
        />
      </>
    ),

    'round-rect': (
      <>
        <GradientDef />
        <rect
          x={2}
          y={2}
          rx={20}
          width={width - 4}
          height={height - 4}
          {...styles}
        />
      </>
    ),

    hexagon: (
      <>
        <GradientDef />
        <path
          d={`M25,2 L${width - 25},2 L${width - 2},${height / 2} L${width - 25},${height - 2} L25,${height - 2} L2,${height / 2} z`}
          {...styles}
        />
      </>
    ),

    diamond: (
      <>
        <GradientDef />
        <path
          d={`M${width / 2},2 L${width - 2},${height / 2} L${width / 2},${height - 2} L2,${height / 2} z`}
          {...styles}
        />
      </>
    ),

    'arrow-rect': (
      <>
        <GradientDef />
        <path
          d={`M2,2 L${width - 12},2 L${width - 2},${height / 2} L${width - 12},${height - 2} L2,${height - 2} z`}
          {...styles}
        />
      </>
    ),

    database: (
      <>
        <GradientDef />
        <path
          d={`M2,${height * 0.125} L2,${height - height * 0.125} A${width / 2 - 2} ${height * 0.125} 0 1 0 ${width - 2} ${height - height * 0.125} L${width - 2},${height * 0.125} A${width / 2 - 2} ${height * 0.125} 0 1 1 2 ${height * 0.125} z`}
          {...styles}
        />
      </>
    ),

    triangle: (
      <>
        <GradientDef />
        <path
          d={`M2,${height - 2} L${width / 2},2 L${width - 2},${height - 2} z`}
          {...styles}
        />
      </>
    ),

    parallelogram: (
      <>
        <GradientDef />
        <path
          d={`M2,${height - 2} L${width * 0.25 + 2},2 L${width - 2},2 L${width - width * 0.25 - 2},${height - 2} z`}
          {...styles}
        />
      </>
    ),

    process: (
      <>
        <GradientDef />
        <rect
          x={2}
          y={2}
          width={width - 4}
          height={height - 4}
          {...styles}
        />
      </>
    ),

    document: (
      <>
        <GradientDef />
        <g>
          <path
            d={`M2,2 L${width - 2},2 L${width - 2},${height - 15} Q${width * 0.75},${height} ${width / 2},${height - 15} Q${width * 0.25},${height - 30} 2,${height - 15} z`}
            {...styles}
          />
        </g>
      </>
    ),

    display: (
      <>
        <GradientDef />
        <g>
          <path
            d={`M25,2 L${width - 25},2 L${width - 2},${height / 2} L${width - 25},${height - 2} L25,${height - 2} L2,${height / 2} z`}
            {...styles}
          />
          <circle
            cx={width / 1.3}
            cy={height / 2}
            r={Math.min(width, height) / 2 - 5}
            {...styles}
          />
        </g>
      </>
    ),

    'manual-operation': (
      <>
        <GradientDef />
        <polygon
          points={`${width * 0.15},2 ${width - width * 0.15},2 ${width - width * 0.25},${height - 2} ${width * 0.25},${height - 2}`}
          {...styles}
        />
      </>
    ),

    'offpage-reference': (
      <>
        <GradientDef />
        <g>
          <rect
            x={2}
            y={2}
            width={width - 4}
            height={height - 4}
            {...styles}
          />
          <polygon
            points={`2,${height - 2} ${width / 2},${height + 28} ${width - 2},${height - 2}`}
            {...styles}
          />
        </g>
      </>
    ),
  };

  return shapes[type] || shapes['round-rect'];
}

// ==================== MAIN COMPONENT ====================
function ProductivityShapeNode({ data, selected, id }) {
  const width = data?.width || 150;
  const height = data?.height || 75;
  const bgColor = data?.color || '#9ca8b3';
  
  const shape = useShape({
    type: data?.shape,
    width,
    height,
    color: bgColor,
    selected,
  });

  const { updateNodeLabel, owner } = useFlowchartStore(
    (state) => ({
      updateNodeLabel: state.updateNodeLabel,
      owner: state.owner,
    }),
    shallow
  );

  // Smart text color based on background
  const textColor = useMemo(() => getTextColor(bgColor), [bgColor]);
  
  // Text shadow for better readability
  const textShadow = useMemo(() => {
    const luminance = getLuminance(bgColor);
    return luminance > 128
      ? '0px 1px 2px rgba(0,0,0,0.3)'
      : '0px 1px 2px rgba(0,0,0,0.5)';
  }, [bgColor]);

  const onChange = useCallback(
    (evt) => {
      updateNodeLabel(id, evt.target.value);
    },
    [id, updateNodeLabel]
  );

  return (
    <div
      style={{
        position: 'relative',
        transition: 'all 0.2s ease',
        transform: selected ? 'scale(1.05)' : 'scale(1)',
      }}
    >
      {/* Connection Handles */}
      <Box>
        {['top', 'right', 'bottom', 'left'].map((pos) => (
          <Handle
            key={pos}
            id={pos}
            style={{
              width: '10px',
              height: '10px',
              backgroundColor: selected ? '#4299e1' : '#000',
              border: '2px solid white',
              boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
              transition: 'all 0.2s ease',
            }}
            position={Position[pos.charAt(0).toUpperCase() + pos.slice(1)]}
            type="source"
          />
        ))}
      </Box>

      {/* Shape SVG */}
      <svg
        style={{
          display: 'block',
          overflow: 'visible',
          filter: selected ? 'drop-shadow(0px 4px 8px rgba(66, 153, 225, 0.4))' : 'none',
        }}
        width={width}
        height={height}
      >
        {shape}
      </svg>

      {/* Text Content */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            width: width * 0.85,
            height: height * 0.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'all',
          }}
        >
          {owner ? (
            <Textarea
              onChange={onChange}
              defaultValue={data?.label}
              placeholder="Type here..."
              className="nodrag"
              color={textColor}
              textAlign="center"
              fontSize="14px"
              fontWeight={600}
              textShadow={textShadow}
              variant="unstyled"
              width="100%"
              height="100%"
              minH={0}
              resize="none"
              p={2}
              _placeholder={{
                color: textColor,
                opacity: 0.5,
              }}
              _focus={{
                outline: 'none',
                boxShadow: 'none',
              }}
              sx={{
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: textColor,
                  borderRadius: '2px',
                  opacity: 0.3,
                },
              }}
            />
          ) : (
            <div
              style={{
                fontFamily: 'system-ui, -apple-system, sans-serif',
                fontWeight: 600,
                color: textColor,
                fontSize: '14px',
                textAlign: 'center',
                textShadow: textShadow,
                padding: '8px',
                wordBreak: 'break-word',
                lineHeight: '1.4',
              }}
            >
              {data?.label || 'Node'}
            </div>
          )}
        </div>
      </div>

      {/* Selection Indicator */}
      {selected && (
        <div
          style={{
            position: 'absolute',
            top: -4,
            left: -4,
            right: -4,
            bottom: -4,
            border: '2px dashed #4299e1',
            borderRadius: '8px',
            pointerEvents: 'none',
            animation: 'pulse 2s ease-in-out infinite',
          }}
        />
      )}
    </div>
  );
}

// Add keyframe animation
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes pulse {
      0%, 100% { opacity: 0.6; }
      50% { opacity: 1; }
    }
  `;
  document.head.appendChild(style);
}

export default ProductivityShapeNode;